import { Spinner } from '@/components/Spinner';
import { searchParametersWithEndpointLabel } from '@/contexts/AreaContext/defaultObjects';
import useApiCall from '@/hooks/useApiCall';
import {
  ButtonIcon,
  Input,
  Select,
  SelectOption,
  Stack,
  Table,
} from '@data-products-and-ai/react-components';
import {
  TableHeaderType,
  TableRowData,
} from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TPartApplicationMatrix,
  TPartLife,
  TPartMaterial,
  TPartScrapRates,
  TPartSetCount,
  TPartStatus,
  TPartWithId,
} from '../types';
import styles from './PartDetailTables.module.scss';
type TLink = {
  baseUrl: string;
  id: string;
  useIdInUrl?: boolean;
  openInNewWindow?: boolean;
};

export const LinkToPage = ({
  baseUrl,
  id,
  useIdInUrl = true,
  openInNewWindow = false,
}: TLink) => {
  return id ? (
    <Link
      to={baseUrl + (useIdInUrl ? id : '')}
      target={openInNewWindow ? '_blank' : ''}
    >
      {id}
    </Link>
  ) : (
    'N/A'
  );
};

export type TTableSize = 'normal' | 'small' | 'extra-small';
type TPartStatusTable = {
  status: TPartStatus;
  size?: TTableSize;
  bom: string;
  editable: boolean;
};

export const PartStatusTable = ({
  status,
  size = 'normal',
  bom,
  editable = true,
}: TPartStatusTable) => {
  const headers: TableHeaderType[] = [
    {
      align: 'left',
      title: 'PDP Status',
    },
    {
      align: 'center',
      title: 'SAP Status',
    },
    {
      align: 'center',
      title: 'Engineering Status',
    },
  ];

  type TEditableCell = {
    type: 'pdp_status' | 'engineering_status';
    status_id: number;
    id: number;
    str: string;
  };

  const EditableCell = (props: TEditableCell) => {
    const [cellState, setCellState] = useState({
      publish: false,
      isEditing: false,
      ...props,
    });

    if (cellState.publish)
      return <Spinner borderSize={2} size={16} color="#CCCCCC" />;

    const Comp = () => {
      const FrameList = () => {
        const { isLoading, data, makeApiCall } = useApiCall<{
          [key: string]: TPartWithId[];
        }>();

        useEffect(() => {
          makeApiCall({
            method: 'GET',
            url:
              import.meta.env.VITE_APP_API_URL +
              '/' +
              searchParametersWithEndpointLabel[props.type].endpoint,
            headers: {
              'Content-Type': 'application/json',
            },
          });
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        type TUpdateCell = {
          id: number;
          pdp_status_id?: number;
          engineering_status_id?: number;
          str: string;
        };

        const UpdateCell = (payload: TUpdateCell) => {
          setCellState((prevState) => {
            return { ...prevState, publish: true };
          });

          const update = async () => {
            try {
              return await makeApiCall({
                method: 'PUT',
                useAsFetch: true,
                url:
                  import.meta.env.VITE_APP_API_URL +
                  `/part/${bom}/${
                    searchParametersWithEndpointLabel[props.type].endpoint
                  }`,
                payload: payload,
                headers: {
                  'Content-Type': 'application/json',
                },
              });
            } catch (error) {
              console.error('Error occurred:', error);

              throw error;
            }
          };

          update()
            .then((result) => {
              if (!result) {
                alert("ERROR: Row couldn't be updated");
                setCellState((prevState) => {
                  return { ...prevState, publish: false };
                });
                return;
              }

              setCellState((prevState) => {
                return {
                  ...prevState,
                  isEditing: false,
                  publish: false,
                  str: payload.str,
                  status_id: payload.pdp_status_id
                    ? payload.pdp_status_id
                    : payload.engineering_status_id
                    ? payload.engineering_status_id
                    : 0,
                };
              });
            })
            .catch((error) => {
              setCellState((prevState) => {
                return { ...prevState, publish: false };
              });
              alert('Something went wrong');
              console.log(error);
            });
        };

        if (isLoading)
          return <Spinner borderSize={2} size={16} color="#CCCCCC" />;

        if (data) {
          return (
            <div style={{ display: 'inline-block' }}>
              <Select
                size="small"
                defaultValue={cellState.status_id.toString()}
                id={cellState.type + cellState.id}
                width={200}
                onChange={(event) =>
                  UpdateCell(
                    props.type === 'pdp_status'
                      ? {
                          id: cellState.id,
                          pdp_status_id: parseInt(event.target.value),
                          str: event.target.options[event.target.selectedIndex]
                            .text,
                        }
                      : {
                          id: cellState.id,
                          engineering_status_id: parseInt(event.target.value),
                          str: event.target.options[event.target.selectedIndex]
                            .text,
                        },
                  )
                }
              >
                {data[
                  searchParametersWithEndpointLabel[props.type].endpoint
                ].map((el: TPartWithId) => {
                  return (
                    <SelectOption
                      key={el.id}
                      label={el.value}
                      value={el.id.toString()}
                    />
                  );
                })}
              </Select>
            </div>
          );
        }

        return '';
      };

      return <FrameList />;
    };
    const EditButton = () => {
      if (cellState.publish) return '';
      return cellState.isEditing ? (
        <ButtonIcon
          hoverType="color"
          icon="IconClose"
          size="extra-small"
          tooltipMessage="Cancel"
          onClick={() =>
            setCellState((prevState) => {
              return { ...prevState, isEditing: false };
            })
          }
        />
      ) : (
        <Stack direction="row">
          <ButtonIcon
            hoverType="color"
            icon="IconEdit"
            size="extra-small"
            tooltipMessage="Edit"
            onClick={() =>
              setCellState((prevState) => {
                return { ...prevState, isEditing: true };
              })
            }
          />
        </Stack>
      );
    };

    return (
      <div>
        {cellState.isEditing ? <Comp /> : cellState.str}
        {editable && (
          <div style={{ display: 'inline-block', marginLeft: 10 }}>
            <EditButton />
          </div>
        )}
      </div>
    );
    //return row.isEditing ? <Comp /> : props.str;
  };

  const rows: TableRowData[] = [
    {
      data: {
        pdp_status: (
          <EditableCell
            type="pdp_status"
            id={status.pdp_status.id}
            status_id={status.pdp_status.pdp_status_id}
            str={status.pdp_status.pdp_status_value}
          />
        ),

        sap_status: status.sap_status,

        engineering_status: (
          <EditableCell
            type="engineering_status"
            id={status.engineering_status.id}
            status_id={status.engineering_status.engineering_status_id}
            str={status.engineering_status.engineering_status_value}
          />
        ),
      },
      id: 'part_status',
    },
  ];

  return (
    <Table
      headerVerticalAlign="bottom"
      headers={headers}
      rows={rows}
      indentation={0}
      typographyTag={
        size === 'normal'
          ? 'textdefault'
          : size === 'extra-small'
          ? 'textextrasmall'
          : 'textsmall'
      }
    />
  );
};
type TPartMaterialTable = {
  material: TPartMaterial;
  size?: TTableSize;
};

export const PartMaterialTable = ({
  material,
  size = 'normal',
}: TPartMaterialTable) => {
  const headers: TableHeaderType[] = [
    {
      align: 'left',
      title: 'Base Material',
    },
    {
      align: 'center',
      title: 'Internal Coating',
    },
    {
      align: 'center',
      title: 'External Coating',
    },
    {
      align: 'center',
      title: 'Thermal Barrier',
    },
  ];
  const rows: TableRowData[] = [
    {
      data: material,
      id: 'material',
    },
  ];

  return (
    <Table
      headerVerticalAlign="bottom"
      headers={headers}
      rows={rows}
      indentation={0}
      typographyTag={
        size === 'normal'
          ? 'textdefault'
          : size === 'extra-small'
          ? 'textextrasmall'
          : 'textsmall'
      }
    />
  );
};

type TPartScrapRatesTable = {
  scrapRates: TPartScrapRates[];
  size?: TTableSize;
};

export const PartScrapRatesTable = ({
  scrapRates,
  size = 'normal',
}: TPartScrapRatesTable) => {
  const headers: TableHeaderType[] = [
    {
      align: 'left',
      title: 'Bom',
    },
    {
      align: 'left',
      title: 'Ambient Temp',
    },
    {
      align: 'left',
      title: 'Concept',
    },
    {
      align: 'center',
      title: 'Tit ISO',
    },
    {
      align: 'center',
      title: 'MRL hrs',
    },
    {
      align: 'center',
      title: 'MRL strts',
    },
    {
      align: 'center',
      title: 'MRL rpr',
    },
  ];

  const rows: TableRowData[] = scrapRates.map((item) => {
    return {
      data: {
        bom: item.bom,
        ambient_temp: item.ambient_temp,
        concept: item.concept,
        tit_iso: item.tit_iso ?? '-',
        mrl_hrs: item.mrl_hrs ?? '-',
        mrl_strts: item.mrl_strts ?? '-',
        mrl_rpr: item.mrl_rpr ?? '-',
      },
      id: item.bom + item.ambient_temp + item.concept,
    };
  });

  return (
    <Table
      headerVerticalAlign="bottom"
      headers={headers}
      rows={rows}
      indentation={0}
      typographyTag={
        size === 'normal'
          ? 'textdefault'
          : size === 'extra-small'
          ? 'textextrasmall'
          : 'textsmall'
      }
    />
  );
};

type TPartLifeTable = {
  partlife: TPartLife[];
  size?: TTableSize;
  bom: string;
  editable: boolean;
};

type TPartLifeWithEditing = TPartLife & {
  isEditing: boolean;
  publish: boolean;
};

export const PartLifeTable = ({
  partlife,
  size = 'normal',
  bom,
  editable = true,
}: TPartLifeTable) => {
  const partLifes: TPartLifeWithEditing[] = partlife.map((item) => ({
    ...item,
    isEditing: false,
    publish: false,
  }));
  const { makeApiCall } = useApiCall<{
    [key: string]: TPartLifeWithEditing[];
  }>();
  const [dynamicValue, setDynamicValue] = useState(partLifes);
  const [dynamicValueMirror, setDynamicValueMirror] = useState(partLifes);

  const headers: TableHeaderType[] = [
    {
      align: 'left',
      title: 'Frame',
      columnWidth: '180px',
    },
    {
      align: 'center',
      title: 'TIT iso [°C]',
    },
    {
      align: 'center',
      title: 'Total Starts [-]',
    },
    {
      align: 'center',
      title: 'Creep Life [kOH]',
    },
    {
      align: 'center',
      title: 'HCF Life [kOH]',
    },
    {
      align: 'center',
      title: 'Int. Oxidation Life [kOH]',
    },
    {
      align: 'center',
      title: 'Status',
    },
    {
      align: 'right',
      title: '',
      columnWidth: '10px',
    },
  ];

  type TEditableCell = {
    id: number;
    str: string;
    partLifeKey: keyof TPartLife;
    type?: 'input' | 'frame' | 'status';
  };

  const setEditing = (id: number, isEditing: boolean) => {
    if (!isEditing) {
      //revert changes in state - cancel was pressed
      setDynamicValueMirror(
        dynamicValue.map((item) => {
          if (item.id === id) {
            return { ...item, isEditing: isEditing, publish: false };
          }
          return item;
        }),
      );
    }
    //change isEditing Flag
    setDynamicValue(
      dynamicValue.map((item) => {
        if (item.id === id) {
          return { ...item, isEditing: isEditing };
        }
        return item;
      }),
    );
  };

  const updateRow = (id: number) => {
    setDynamicValue((prevDynamicValue) => {
      return prevDynamicValue.map((item) => {
        if (item.id === id) {
          return { ...item, publish: true, isEditing: false };
        }
        return item;
      });
    });
  };

  const UpdatePartLife = (partlifeRow: TPartLifeWithEditing) => {
    const update = async () => {
      try {
        return await makeApiCall({
          method: 'PUT',
          useAsFetch: true,
          url: import.meta.env.VITE_APP_API_URL + `/part/${bom}/part_lifes`,
          payload: partlifeRow,
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Error occurred:', error);

        throw error;
      }
    };

    update()
      .then((result) => {
        if (!result) {
          alert("ERROR: Row couldn't be updated");
          return;
        }

        setDynamicValue((prevDynamicValue) => {
          return prevDynamicValue.map((item) => {
            if (item.id === partlifeRow.id) {
              return partlifeRow;
            }
            return item;
          });
        });

        /*  setIsEditing(false);
      setDescription(currentDescription); */
      })
      .catch((error) => {
        alert('Something went wrong');
        console.log(error);
      });
  };
  useEffect(() => {
    const publishRow = dynamicValue.find((item) => item.publish === true);
    if (!publishRow) return;
    const editedRow = dynamicValueMirror.find(
      (item) => item.id === publishRow.id,
    );

    if (!editedRow) return;

    UpdatePartLife(editedRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicValue]);

  const updateCell = (
    id: number,
    str: string,
    partLifeKey: keyof TPartLife,
  ) => {
    setDynamicValueMirror((prevState) => {
      return prevState.map((item) => {
        if (item.id === id) {
          return { ...item, [partLifeKey]: str };
        }
        return item;
      });
    });
  };

  const EditableCell = ({ type = 'input', ...props }: TEditableCell) => {
    const row = dynamicValue.find((item) => item.id === props.id);
    if (!row) return null;

    if (row.publish)
      return <Spinner borderSize={2} size={16} color="#CCCCCC" />;

    const Comp = () => {
      if (type === 'status') {
        const arrStatus = [
          'none',
          'estimated',
          'preliminary',
          'released',
          'test',
        ];
        return (
          <Select
            size="small"
            defaultValue={props.str}
            id={props.partLifeKey + props.id}
            onChange={(event) =>
              updateCell(props.id, event.target.value, props.partLifeKey)
            }
            placeholder="Status"
          >
            {arrStatus.map((el: string) => {
              return <SelectOption key={el} label={el} value={el} />;
            })}
          </Select>
        );
      }

      if (type === 'frame') {
        const FrameList = () => {
          const { isLoading, error, data, makeApiCall } = useApiCall<{
            [key: string]: string[];
          }>();

          useEffect(() => {
            makeApiCall({
              method: 'GET',
              url: import.meta.env.VITE_APP_API_URL + '/applications',
              headers: {
                'Content-Type': 'application/json',
              },
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, []);

          if (isLoading)
            return <Spinner borderSize={2} size={16} color="#CCCCCC" />;
          if (error) return 'Error';

          if (data) {
            return (
              <div>
                <Select
                  size="small"
                  defaultValue={props.str}
                  id={props.partLifeKey + props.id}
                  onChange={(event) =>
                    updateCell(props.id, event.target.value, props.partLifeKey)
                  }
                  placeholder="Frame"
                >
                  {data.applications.map((el: string) => {
                    return <SelectOption key={el} label={el} value={el} />;
                  })}
                </Select>
              </div>
            );
          }

          return '';
        };

        /* const result = item.frame.substring(0, item.frame.indexOf('('));
        result ? (
          <LinkToPage baseUrl={`/frames/${result}/`} id={item.frame} />
        ) : (
          item.frame
        ) */

        return <FrameList />;
      }

      return (
        <Input
          size="small"
          id={props.partLifeKey + props.id}
          defaultValue={props.str}
          onChange={(event) =>
            updateCell(props.id, event.target.value, props.partLifeKey)
          }
        />
      );
    };

    return row.isEditing ? <Comp /> : props.str;
  };

  const EditButton = (item: TPartLifeWithEditing) => {
    if (item.publish) return '';
    return item.isEditing ? (
      <Stack direction="row">
        <ButtonIcon
          icon="IconClose"
          size="extra-small"
          tooltipMessage="Cancel"
          hoverType="color"
          onClick={() => setEditing(item.id, false)}
        />

        <ButtonIcon
          hoverType="color"
          icon="IconCheck"
          size="extra-small"
          tooltipMessage="Confirm"
          onClick={() => updateRow(item.id)}
        />
      </Stack>
    ) : (
      <Stack direction="row">
        {/*  <ButtonIcon
          hoverType="color"
          icon="IconTrash"
          size="extra-small"
          tooltipMessage="Delete"
          disabled={
            dynamicValue.filter((item) => item.isEditing === true).length > 0
          }
          onClick={() => setEditing(item.id, true)}
        /> */}
        <ButtonIcon
          hoverType="color"
          icon="IconEdit"
          size="extra-small"
          tooltipMessage="Edit"
          disabled={
            dynamicValue.filter((item) => item.isEditing === true).length > 0
          }
          onClick={() => setEditing(item.id, true)}
        />
      </Stack>
    );
  };
  const rows: TableRowData[] = dynamicValue.map((item, index: number) => {
    return {
      data: {
        frame: (
          <EditableCell
            key={item.id + 'frame'}
            id={item.id}
            str={item.frame}
            partLifeKey="frame"
            type="frame"
          />
        ),

        tit_iso: (
          <EditableCell
            key={item.id + 'tit_ison'}
            id={item.id}
            str={item.tit_iso}
            partLifeKey="tit_iso"
          />
        ),
        total_starts: (
          <EditableCell
            id={item.id}
            str={item.total_starts}
            partLifeKey="total_starts"
          />
        ),
        hcf: <EditableCell id={item.id} str={item.hcf} partLifeKey="hcf" />,
        int_oxidation: (
          <EditableCell
            id={item.id}
            str={item.int_oxidation}
            partLifeKey="int_oxidation"
          />
        ),
        ext_oxidation: (
          <EditableCell
            id={item.id}
            str={item.ext_oxidation}
            partLifeKey="ext_oxidation"
          />
        ),
        status: (
          <EditableCell
            id={item.id}
            str={item.status}
            partLifeKey="status"
            type="status"
          />
        ),
        edit: editable ? <EditButton {...item} /> : '',
      },
      id: `row_${index}`,
    };
  });

  const memoizedRows = React.useMemo(() => {
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicValue]);

  const memoizedTable = React.useMemo(() => {
    return (
      <Table
        headers={headers}
        rows={memoizedRows}
        headerVerticalAlign="bottom"
        indentation={0}
        typographyTag={
          size === 'normal'
            ? 'textdefault'
            : size === 'extra-small'
            ? 'textextrasmall'
            : 'textsmall'
        }
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedRows]);

  return <>{memoizedTable}</>;
};

type TPartSetCountTable = {
  setcount: TPartSetCount[];
  size?: TTableSize;
};
export const PartSetCountTable = ({
  setcount,
  size = 'normal',
}: TPartSetCountTable) => {
  const headers: TableHeaderType[] = [
    {
      align: 'left',
      title: 'Path',
    },
    {
      align: 'left',
      title: 'Description',
    },
    {
      align: 'center',
      title: 'Material Master',
    },
    {
      align: 'center',
      title: 'Number of Sets / Parts',
    },
  ];
  const rows: TableRowData[] = setcount.map((item) => {
    return {
      data: {
        path: item.path,
        description: item.description,
        material_master: item.material_master,
        count: item.count,
      },
      id: item.path + item.count,
    };
  });

  return (
    <Table
      headerVerticalAlign="bottom"
      headers={headers}
      rows={rows}
      indentation={0}
      typographyTag={
        size === 'normal'
          ? 'textdefault'
          : size === 'extra-small'
          ? 'textextrasmall'
          : 'textsmall'
      }
    />
  );
};

type TPartApplicationMatrixTable = {
  applicationmatrix: Partial<TPartApplicationMatrix>[];
  size?: TTableSize;
  compare?: boolean;
};
export const PartApplicationMatrixTable = ({
  applicationmatrix,
  compare = false,
  size = 'normal',
}: TPartApplicationMatrixTable) => {
  let headers: TableHeaderType[] = [];
  if (compare) {
    headers = [
      {
        align: 'left',
        title: 'Main Cluster',
      },
      {
        align: 'center',
        title: 'Incoming Set-Style',
      },
      {
        align: 'center',
        title: 'Incoming Sub-Style',
      },
      {
        align: 'center',
        title: 'Characteristic [in]',
      },
      {
        align: 'center',
        title: 'Life Cluster [in]',
      },
      {
        align: 'center',
        title: 'Assembly Cluster [in]',
      },

      {
        align: 'center',
        title: 'Status',
      },
    ];
  } else {
    headers = [
      {
        align: 'left',
        title: 'Main Cluster',
      },
      {
        align: 'center',
        title: 'Incoming Set-Style',
      },
      {
        align: 'center',
        title: 'Incoming Sub-Style',
      },
      {
        align: 'center',
        title: 'Characteristic [in]',
      },
      {
        align: 'center',
        title: 'Life Cluster [in]',
      },
      {
        align: 'center',
        title: 'Assembly Cluster [in]',
      },

      {
        align: 'center',
        title: 'Outcoming Set-Style',
      },
      {
        align: 'center',
        title: 'Outcoming Sub-Style',
      },
      {
        align: 'center',
        title: 'Characteristic [out]',
      },
      {
        align: 'center',
        title: 'Life Cluster [out]',
      },
      {
        align: 'center',
        title: 'Assembly Cluster [out]',
      },
      {
        align: 'center',
        title: 'Status',
      },
    ];
  }

  const rows: TableRowData[] = applicationmatrix.map((item, index: number) => {
    if (compare) {
      return {
        data: {
          main_cluster: item.main_cluster,
          incoming_set_style: item.incoming_set_style,
          incoming_sub_style: item.incoming_sub_style,
          characteristic_in: item.characteristic_in,
          life_cluster_in: (
            <div
              className={styles['matrix-cell']}
              style={{
                backgroundColor: item.life_cluster_in_color,
                justifyContent: 'center',
              }}
            >
              {item.life_cluster_in}
            </div>
          ),
          assembly_cluster_in: (
            <div
              className={styles['matrix-cell']}
              style={{
                backgroundColor: item.assembly_cluster_in_color,
                justifyContent: 'center',
              }}
            >
              {item.assembly_cluster_in}
            </div>
          ),

          status: item.status,
        },
        id: `row_${index}`,
      };
    } else {
      return {
        data: {
          main_cluster: item.main_cluster,
          incoming_set_style: item.incoming_set_style,
          incoming_sub_style: item.incoming_sub_style,
          characteristic_in: item.characteristic_in,
          life_cluster_in: (
            <div
              className={styles['matrix-cell']}
              style={{
                backgroundColor: item.life_cluster_in_color,
                justifyContent: 'center',
              }}
            >
              {item.life_cluster_in}
            </div>
          ),
          assembly_cluster_in: (
            <div
              className={styles['matrix-cell']}
              style={{
                backgroundColor: item.assembly_cluster_in_color,
                justifyContent: 'center',
              }}
            >
              {item.assembly_cluster_in}
            </div>
          ),
          outcoming_set_style: item.outcoming_set_style,
          outcoming_sub_style: item.outcoming_sub_style,
          characteristic_out: item.characteristic_out,
          life_cluster_out: (
            <div
              className={styles['matrix-cell']}
              style={{
                backgroundColor: item.life_cluster_out_color,
                justifyContent: 'center',
              }}
            >
              {item.life_cluster_out}
            </div>
          ),
          assembly_cluster_out: (
            <div
              className={styles['matrix-cell']}
              style={{
                backgroundColor: item.assembly_cluster_out_color,
                justifyContent: 'center',
              }}
            >
              {item.assembly_cluster_out}
            </div>
          ),
          status: item.status,
        },
        id: `row_${index}`,
      };
    }
  });

  return (
    <Table
      headerVerticalAlign="bottom"
      headers={headers}
      rows={rows}
      indentation={0}
      typographyTag={
        size === 'normal'
          ? 'textdefault'
          : size === 'extra-small'
          ? 'textextrasmall'
          : 'textsmall'
      }
    />
  );
};
